// Purchase order rules shared by the server (mutations, MCP, PDF) and the browser (instant edits,
// warnings), so both always agree on prices, quantities, totals and what a PO may do next. The
// mutations' field schemas live here too; shared/mutations.ts lists the mutations.
import { z } from "zod";

export const PO_STATUSES = ["draft", "sent", "acknowledged", "in_production", "shipped", "received", "closed", "cancelled"] as const;
export type PoStatus = (typeof PO_STATUSES)[number];
export const PO_STATUS_LABELS: Record<PoStatus, string> = {
  draft: "Draft",
  sent: "Sent",
  acknowledged: "Acknowledged",
  in_production: "In production",
  shipped: "Shipped",
  received: "Received",
  closed: "Closed",
  cancelled: "Cancelled",
};

/** Kinds a PO can be created as. Make POs (a CM building a BOM) come through po.create_make (consignment). */
export const PO_KINDS = ["purchase", "service"] as const;
export const PO_KIND_LABELS = { purchase: "Purchase", service: "Service", make: "Make" } as const;

/**
 * Where a PO can go from each status: forward through the lifecycle, back one step to undo a
 * mis-click, and cancelled until goods ship. Draft → sent goes through "Mark as sent".
 */
export const NEXT_STATUSES: Record<PoStatus, readonly PoStatus[]> = {
  draft: ["sent", "cancelled"],
  sent: ["acknowledged", "in_production", "shipped", "cancelled"],
  acknowledged: ["in_production", "shipped", "sent", "cancelled"],
  in_production: ["shipped", "acknowledged", "cancelled"],
  shipped: ["received", "in_production"],
  received: ["closed", "shipped"],
  closed: ["received"],
  cancelled: ["draft"],
};
export const canMoveTo = (from: PoStatus, to: PoStatus) => NEXT_STATUSES[from].includes(to);

/** Lines (items, quantities, prices) can change until the goods ship. */
export const linesEditable = (status: PoStatus) => status === "draft" || status === "sent" || status === "acknowledged" || status === "in_production";
/**
 * A PO from paper (historical) is the record of what was invoiced: its lines are filled in or put right as the paper
 * says, on the PO itself (Mitch, 2026-11-04: "Why can't it just open up the PO so I can edit it?"), quietly (no new
 * revision, nothing sent or incoming, every line delivered). A paper make PO's builds stay as they were recorded.
 */
export const paperLines = (po: { historical: boolean; kind: string }) => po.historical && po.kind !== "make";
/** Whether a PO's lines can change now: until its goods ship, or any time on paper (`paperLines`). */
export const poLinesEditable = (po: { status: PoStatus; historical: boolean; kind: string }) => linesEditable(po.status) || paperLines(po);
/** The header (dates, notes, terms) can change until the PO is closed or cancelled. */
export const headerEditable = (status: PoStatus) => status !== "closed" && status !== "cancelled";
/** POs that still count as on order. */
export const isOpen = (status: PoStatus) => status !== "closed" && status !== "cancelled" && status !== "received";

type DraftRow = { id: string; vendor_id: string; status: PoStatus; kind: string; ship_to_location_id: string | null; created_at: string; auto_key?: string };
/**
 * The draft to add a vendor's lines to: its newest draft PO of this kind shipping to this place (a PO with no ship-to
 * ships to `home`, HHL's default), a person's before one auto mode made; null when there's none and one should be
 * started. A make draft (or one shipping to the CM) never takes a purchase line, and the other way round.
 */
export function draftFor<P extends DraftRow>(pos: readonly P[], vendorId: string, shipTo: string | null, kind: string, home: string | null = null): P | null {
  const place = (id: string | null) => id ?? home;
  return (
    pos
      .filter((p) => p.vendor_id === vendorId && p.status === "draft" && p.kind === kind && place(p.ship_to_location_id) === place(shipTo))
      .sort((a, b) => Number(!!a.auto_key) - Number(!!b.auto_key) || String(b.created_at).localeCompare(String(a.created_at)))[0] ?? null
  );
}

/** PO fields the vendor sees: changing one after a send starts a new revision. */
export const VENDOR_VISIBLE_FIELDS = [
  "kind",
  "ship_to_location_id",
  "currency",
  "payment_terms",
  "incoterms",
  "deposit_pct",
  "expected_ship",
  "expected_arrive",
  "notes",
] as const;

type Revisioned = { revision: number; sent_revision: number | null };
/** The vendor has the current revision, so the next change they'd see starts a new one. */
export const startsNewRevision = (po: Revisioned) => po.sent_revision !== null && po.revision === po.sent_revision;
/** Changed since the vendor got it. */
export const hasUnsentChanges = (po: Revisioned) => po.sent_revision !== null && po.revision > po.sent_revision;

// Money -------------------------------------------------------------------------------------------
// Unit prices can have 4+ decimals (0.0985), quantities 3. Multiplying doubles drifts (1.005 * 100),
// so amounts are worked out on scaled integers and rounded half away from zero, like Postgres' round().

const SCALE = 8;
const scaled = (n: number) => BigInt(n.toFixed(SCALE).replace(".", ""));

/** a × b rounded to `places` decimals. */
export function mulRound(a: number, b: number, places = 2): number {
  const product = scaled(a) * scaled(b); // scaled by 10^(2·SCALE)
  const drop = 10n ** BigInt(2 * SCALE - places);
  const negative = product < 0n;
  const abs = negative ? -product : product;
  const rounded = (abs + drop / 2n) / drop;
  return (negative ? -Number(rounded) : Number(rounded)) / 10 ** places;
}

/** Adds money amounts without float drift. */
export const sumMoney = (values: number[]) => values.reduce((total, v) => mulRound(total + v, 1, 2), 0);

/** qty × unit price in cents, or null while the line has no price. */
export const lineAmount = (qty: number, unitPrice: number | null) => (unitPrice == null ? null : mulRound(qty, unitPrice, 2));

export type PoTotals = { lines: number; qty: number; subtotal: number; deposit: number; balance: number; unpriced: number };

export function poTotals(lines: { qty: number; unit_price: number | null }[], depositPct: number): PoTotals {
  const amounts = lines.map((l) => lineAmount(Number(l.qty), l.unit_price == null ? null : Number(l.unit_price)));
  const subtotal = sumMoney(amounts.filter((a): a is number => a != null));
  const deposit = mulRound(subtotal, Number(depositPct) / 100, 2);
  return {
    lines: lines.length,
    qty: lines.reduce((n, l) => mulRound(n + Number(l.qty), 1, 3), 0),
    subtotal,
    deposit,
    balance: mulRound(subtotal - deposit, 1, 2),
    unpriced: amounts.filter((a) => a == null).length,
  };
}

// Vendor terms --------------------------------------------------------------------------------------

export type PriceBreak = { min_qty: number; unit_price: number };
/** What a PO line needs from an item's vendor link (vendor_item). */
export type VendorTerms = {
  vendor_sku: string;
  vendor_name: string;
  unit_price: number | null;
  currency: string;
  price_breaks: unknown;
  moq: number;
  order_multiple: number;
  lead_time_days: number | null;
};

const breaksOf = (terms: VendorTerms) =>
  ((Array.isArray(terms.price_breaks) ? terms.price_breaks : []) as PriceBreak[])
    .filter((b) => b && Number(b.min_qty) > 0 && b.unit_price != null)
    .map((b) => ({ min_qty: Number(b.min_qty), unit_price: Number(b.unit_price) }))
    .sort((a, b) => a.min_qty - b.min_qty);

/**
 * The unit price for a quantity: the biggest price break the quantity reaches, else the base price,
 * else (below every break, no base price) the first break's price.
 */
export function priceFor(terms: VendorTerms | undefined, qty: number): number | null {
  if (!terms) return null;
  const breaks = breaksOf(terms);
  const reached = breaks.filter((b) => qty >= b.min_qty).at(-1);
  if (reached) return reached.unit_price;
  if (terms.unit_price != null) return Number(terms.unit_price);
  return breaks[0]?.unit_price ?? null;
}

/**
 * The price a line should have after its quantity changes, or undefined to keep the price it has.
 * A price someone typed over the vendor's price stays; a price that came from the vendor follows the breaks.
 */
export function repriceFor(terms: VendorTerms | undefined, line: { qty: number; unit_price: number | null }, nextQty: number): number | null | undefined {
  if (!terms) return undefined;
  const fromVendor = line.unit_price == null || line.unit_price === priceFor(terms, Number(line.qty));
  if (!fromVendor) return undefined;
  const next = priceFor(terms, nextQty);
  return next === line.unit_price ? undefined : next;
}

const multipleOf = (terms: VendorTerms) => (Number(terms.order_multiple) > 0 ? Number(terms.order_multiple) : 1);
/** How many multiples fit in qty, ignoring float noise (0.3 / 0.1 is 2.9999999999999996). */
const ratio = (qty: number, multiple: number) => {
  const r = qty / multiple;
  const near = Math.round(r);
  return Math.abs(r - near) <= 1e-9 * Math.max(1, Math.abs(r)) ? near : r;
};

/** The smallest quantity of at least `wanted` that meets the MOQ and is a whole number of order multiples. */
export function validQty(terms: VendorTerms | undefined, wanted = 0): number {
  const multiple = terms ? multipleOf(terms) : 1;
  const floor = Math.max(wanted, terms ? Number(terms.moq) : 0, 0);
  if (floor === 0) return multiple;
  return mulRound(Math.max(1, Math.ceil(ratio(floor, multiple))), multiple, 6);
}

export type QtyWarning = { code: "moq" | "multiple"; message: string; fix: number };

/** Why a quantity doesn't suit the vendor, with the quantity that would. */
export function qtyWarnings(terms: VendorTerms | undefined, qty: number): QtyWarning[] {
  if (!terms) return [];
  const out: QtyWarning[] = [];
  const fix = validQty(terms, qty);
  const moq = Number(terms.moq);
  const multiple = multipleOf(terms);
  const fmt = (n: number) => n.toLocaleString("en-US", { maximumFractionDigits: 3 });
  if (moq > 0 && qty < moq) out.push({ code: "moq", message: `Below the MOQ of ${fmt(moq)}`, fix });
  if (multiple !== 1 && !Number.isInteger(ratio(qty, multiple))) {
    out.push({ code: "multiple", message: `Not a multiple of ${fmt(multiple)}`, fix });
  }
  return out;
}

// Fields -------------------------------------------------------------------------------------------

const text = (max = 200) => z.string().trim().max(max);
const isoDate = z.iso.date("Use a date like 2026-12-31").nullable();
const depositPct = z.number().min(0, "Deposit is a percentage from 0 to 100").max(100, "Deposit is a percentage from 0 to 100");
const email = z.union([z.literal(""), z.email("Enter a valid email").trim().toLowerCase()]);

/** PO header fields people edit (status, number and revision change only through their own mutations). */
export const poFields = z.object({
  kind: z.enum(PO_KINDS),
  ship_to_location_id: z.uuid().nullable(),
  currency: z.string().trim().toUpperCase().length(3, "Use a 3-letter currency code"),
  payment_terms: text(),
  incoterms: text(50),
  deposit_pct: depositPct,
  expected_ship: isoDate,
  expected_arrive: isoDate,
  vendor_ref: text(100),
  notes: text(5000),
  internal_notes: text(5000),
  // Inbound tracking --- HHL's own record of how it travels (never printed or a new revision).
  carrier: text(50),
  tracking_number: text(100),
  shipped_on: isoDate,
});

/** Inbound tracking --- what Move to → Shipped asks for (all optional). */
export const shippingInput = z.object({ carrier: text(50), tracking_number: text(100), shipped_on: isoDate }).partial();

// Carriers and their tracking pages live in shared/carriers.ts (no zod: the vendor portal's page uses them too).
export { CARRIERS, trackingUrl } from "./carriers.ts";

export const poLineFields = z.object({
  qty: z.number().positive("Quantity must be more than 0"),
  unit_price: z.number().nonnegative("Price must be 0 or more").nullable(),
  vendor_sku: text(100),
  vendor_name: text(300),
  promised_date: isoDate,
  notes: text(2000),
  position: z.number().int(),
});

/**
 * Check against the plan --- what the plan said for a line when it was drafted from the plan (Create draft POs, auto
 * mode) or last updated from it (the check's Update selected): po_line.plan_basis. The check (shared/po-plan-check.ts)
 * explains what changed since from it, and a qty other than `qty` was changed by hand. Rounded to keep rows small.
 */
export const planBasisSchema = z.object({
  /** Who set it: drafted from the plan, by auto mode, or updated through the check. */
  via: z.enum(["plan", "auto", "check"]),
  /** The plan's day. */
  at: z.iso.date(),
  /** The quantity the plan gave the line (after MOQ and multiples), and the need before them. */
  qty: z.number().nonnegative(),
  raw: z.number().nonnegative(),
  available: z.number(),
  /** Other open POs it counted (drafts too), by number. */
  incoming: z.array(z.object({ po: z.string().max(40), qty: z.number() })).max(20),
  /** Forecast units a day over the next 90 days. */
  rate: z.number().nonnegative(),
  b2b: z.number(),
  /** Builds' demand for it over the next year (parts in BOMs). */
  dependent: z.number(),
  order_by: z.iso.date().nullable(),
  safety: z.number(),
  moq: z.number().nonnegative(),
  multiple: z.number().positive(),
  lead: z.number(),
  /** Vendor closures --- days a closure added to it (ItemPlanResult.delay); missing on lines drafted before closures. */
  closed: z.number().nonnegative().optional(),
});
export type PlanBasis = z.infer<typeof planBasisSchema>;

/** A line to add. Only the item is needed: quantity, price and the snapshot default from the vendor link. */
export const poLineInput = z.object({
  id: z.uuid().optional(),
  item_id: z.uuid(),
  ...poLineFields.partial().shape,
  sku: text(255).optional(),
  name: text(500).optional(),
  photo_url: text(1000).optional(),
  plan_basis: planBasisSchema.optional(), // Check against the plan ---
});

export const poCreateArgs = z.object({
  id: z.uuid(),
  vendor_id: z.uuid(),
  kind: z.enum(PO_KINDS).optional(),
  ship_to_location_id: z.uuid().nullable().optional(),
  expected_ship: isoDate.optional(),
  expected_arrive: isoDate.optional(),
  notes: text(5000).optional(),
  /** Production runs --- the run these parts are bought for (it's linked to it). */
  production_run_id: z.uuid().optional(),
  /** Cover through --- the day its maker was buying to last to (migration 061); its check against the plan plans with it. */
  cover_through: isoDate.optional(),
  lines: z.array(poLineInput).max(500).default([]),
});

/** A place POs ship to: the address and contact printed on the PO. */
export const shipToFields = z.object({
  name: z.string().trim().min(1, "Name is required").max(200),
  /** The address as one block of text, from before its fields (migration 077): moved into them, then left empty. */
  address: text(1000),
  // Addresses --- a site's address in its fields (shared/address.ts)
  address_line1: text(),
  address_line2: text(),
  address_district: text(),
  address_city: text(),
  address_region: text(),
  address_postal_code: text(30),
  address_country: z.string().trim().toUpperCase().max(2),
  contact_name: text(),
  contact_phone: text(50),
  contact_email: email,
  notes: text(5000),
});

export const vendorPoFields = z.object({ deposit_pct: depositPct, default_ship_to_location_id: z.uuid().nullable() });

/** A new line's snapshot and price from the item and its link to the PO's vendor. */
export function lineDefaults(
  item: { sku: string; name: string; photo_url: string },
  terms: VendorTerms | undefined,
  qty: number,
): { sku: string; name: string; vendor_sku: string; vendor_name: string; photo_url: string; unit_price: number | null } {
  return {
    sku: item.sku,
    name: item.name,
    vendor_sku: terms?.vendor_sku ?? "",
    vendor_name: terms?.vendor_name ?? "",
    photo_url: item.photo_url,
    unit_price: priceFor(terms, qty),
  };
}

/**
 * An accepted invoice price is the cost (migration 067): PO lines as costs see them, each at its billed price when its
 * bills set one (po_line.billed_price), else its own. The same array for the same rows (so caches keyed on it hold).
 */
const costed = new WeakMap<readonly object[], readonly object[]>();
export function costedPoLines<L extends { unit_price: number | null; billed_price?: number | null }>(rows: readonly L[]): L[] {
  const hit = costed.get(rows);
  if (hit) return hit as L[];
  const out = rows.some((l) => l.billed_price != null) ? rows.map((l) => (l.billed_price != null ? { ...l, unit_price: l.billed_price } : l)) : rows;
  costed.set(rows, out);
  return out as L[];
}

/**
 * When a PO was ordered and when its goods arrived, and the days between (migration 078; Mitch: "that's the stuff which
 * is important to be able to extrapolate the lead times"). Ordered: the day on paper it went (`sent_on`), else when it
 * was marked sent here. Received: the day on paper (`received_on`), else, once it's all in (received or closed), its
 * last receipt's day. Either may be null (not known yet).
 */
export function poDates(
  po: { sent_on?: string | null; sent_at?: string | null; dated_on?: string | null; received_on?: string | null; status: string; historical?: boolean },
  receipts: readonly { received_at: string }[] = [],
): { ordered: string | null; received: string | null; days: number | null } {
  const day = (v: string | null | undefined) => (v ? String(v).slice(0, 10) : null);
  const ordered = day(po.sent_on) ?? day(po.sent_at) ?? (po.historical ? day(po.dated_on) : null);
  const last =
    receipts
      .map((r) => day(r.received_at))
      .filter((d): d is string => !!d)
      .sort()
      .at(-1) ?? null;
  const received = day(po.received_on) ?? (po.status === "received" || po.status === "closed" ? last : null);
  const days = ordered && received ? Math.round((Date.parse(received) - Date.parse(ordered)) / 86_400_000) : null;
  return { ordered, received, days: days != null && days >= 0 ? days : null };
}
